/**
 * Pure helpers for the map overlays. Kept free of three.js so they can be
 * unit-tested without a renderer.
 */

export interface BusynessSample {
  /** Projected metre-space position. */
  readonly x: number;
  readonly z: number;
  /** Parcel busyness on the 0..3 scale the simulation uses. */
  readonly busyness: number;
}

/** Nothing nearby reads as a quietly-used street, not a dead one. */
const FALLBACK_BUSYNESS = 0.4;
const RANGE_M = 120;

/**
 * Busyness at a point, as an inverse-distance-weighted mean of the parcels
 * within range. Streets between two busy frontages glow along their whole
 * length; a road out of town fades toward the fallback.
 */
export function busynessAt(
  x: number,
  z: number,
  samples: readonly BusynessSample[],
): number {
  let weight = 0;
  let sum = 0;
  for (const s of samples) {
    const d = Math.hypot(s.x - x, s.z - z);
    if (d > RANGE_M) continue;
    const w = 1 / (d + 10);
    weight += w;
    sum += s.busyness * w;
  }
  return weight > 0 ? sum / weight : FALLBACK_BUSYNESS;
}
